import React, { useEffect, useMemo, useState } from "react";

import { ArrowLeftRight, ChevronLeft, Play } from "lucide-react";

import FilterChips from "./FilterChips";
import {
  ACCOUNT_TEAM_ASSIGNMENT_ROLES,
  DEFAULT_ACCOUNT_TEAM_SUBROLE,
  isVideoAccountTeamTopRole,
  VIDEO_ACCOUNT_TEAM_SUBROLE_OPTIONS,
  VIDEO_ROLE_ACCORDIONS,
  VIDEO_ROLE_TOP_OPTIONS,
} from "./videoOrderUtils";
import WorkspaceSearch from "./WorkspaceSearch";
import Button from "../../Components/Button";
import CustomCheckbox from "../../Components/CheckCircle";
import CommonModal from "../../Components/Modal";
import ToggleSwitch from "../../Components/ToggleSwitch";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  assignLibraryVideos,
  deleteVideo,
  fetchAdminVideos,
  fetchLibraryVideos,
  Video,
  VideoTopic,
} from "../../Store/Reducers/VideoSlice";
import { ACCOUNT_TEAM_ROLE_FILTER } from "../../Utils/Constants/RoleConstants";
import { toast } from "../../Utils/Helper/toast";
import { UNCATEGORIZED_TOPIC_KEY } from "../../Utils/Helper/videoTopics";
import { getYouTubeThumbnailUrl } from "../../Utils/Helper/videoUrl";

type WizardStep = "role" | "videos";

export interface AssignBrowseTarget {
  topRole: string;
  subRole: string;
}

interface AssignVideosModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Current browse role — preselected when the wizard opens. */
  initialTopRole: string;
  initialSubRole: string;
  /** Role playlists keyed by login role (for baseline + counts). */
  videosByRole: Record<string, Video[]>;
  /** After a successful save, sync the page browse dropdown. */
  onAssigned?: (target: AssignBrowseTarget) => void;
}

/** Concrete Account Team login roles (All is not offered). */
const ACCOUNT_TEAM_SUBROLE_OPTIONS = VIDEO_ACCOUNT_TEAM_SUBROLE_OPTIONS;

const getTopRoleLabel = (roleKey: string) =>
  VIDEO_ROLE_TOP_OPTIONS.find(role => role.value === roleKey)?.label || roleKey;

const getLoginRoleLabel = (roleKey: string) =>
  VIDEO_ROLE_ACCORDIONS.find(role => role.value === roleKey)?.label ||
  VIDEO_ACCOUNT_TEAM_SUBROLE_OPTIONS.find(role => role.value === roleKey)
    ?.label ||
  getTopRoleLabel(roleKey);

/** Expand Step 1 picks into concrete login roles (one playlist each). */
const resolveRoleKeys = (
  selectedTopRoles: string[],
  accountTeamSubRoles: string[]
): string[] => {
  const keys: string[] = [];
  selectedTopRoles.forEach(top => {
    if (isVideoAccountTeamTopRole(top)) {
      keys.push(
        ...accountTeamSubRoles.filter(role =>
          ACCOUNT_TEAM_ASSIGNMENT_ROLES.includes(role)
        )
      );
    } else if (top) {
      keys.push(top);
    }
  });
  return Array.from(new Set(keys));
};

const browseTargetFromSelection = (
  selectedTopRoles: string[],
  accountTeamSubRoles: string[],
  preferredRole?: string
): AssignBrowseTarget => {
  if (preferredRole) {
    const isAt = ACCOUNT_TEAM_ASSIGNMENT_ROLES.includes(preferredRole);
    if (isAt) {
      return {
        topRole: ACCOUNT_TEAM_ROLE_FILTER,
        subRole: preferredRole,
      };
    }
    return {
      topRole: preferredRole,
      subRole: DEFAULT_ACCOUNT_TEAM_SUBROLE,
    };
  }
  const first = selectedTopRoles[0];
  if (first && isVideoAccountTeamTopRole(first)) {
    return {
      topRole: ACCOUNT_TEAM_ROLE_FILTER,
      subRole: accountTeamSubRoles[0] || DEFAULT_ACCOUNT_TEAM_SUBROLE,
    };
  }
  return {
    topRole: first || VIDEO_ROLE_TOP_OPTIONS[0]?.value || "vendor_admin",
    subRole: DEFAULT_ACCOUNT_TEAM_SUBROLE,
  };
};

const libraryIdsForRole = (
  videosByRole: Record<string, Video[]>,
  role: string
): string[] => {
  const ids: string[] = [];
  (videosByRole[role] || []).forEach(video => {
    if (video.library_video_id && !ids.includes(video.library_video_id)) {
      ids.push(video.library_video_id);
    }
  });
  return ids;
};

const assignmentIdsForRoleLibrary = (
  videosByRole: Record<string, Video[]>,
  role: string,
  libraryId: string
): string[] =>
  (videosByRole[role] || [])
    .filter(video => video.library_video_id === libraryId)
    .map(video => video.video_id);

const sortedKey = (ids: string[]) => [...ids].sort().join("|");

const AssignVideosModal: React.FC<AssignVideosModalProps> = ({
  isOpen,
  onClose,
  initialTopRole,
  initialSubRole,
  videosByRole,
  onAssigned,
}) => {
  const dispatch = useAppDispatch();
  const {
    libraryVideos,
    topics,
    librarySaving: saving,
    deleteVideoLoading,
  } = useAppSelector(state => state.video);

  const [step, setStep] = useState<WizardStep>("role");
  const [selectedTopRoles, setSelectedTopRoles] = useState<string[]>([]);
  const [accountTeamSubRoles, setAccountTeamSubRoles] = useState<string[]>([
    ...ACCOUNT_TEAM_ASSIGNMENT_ROLES,
  ]);
  const [confirmedRoleKeys, setConfirmedRoleKeys] = useState<string[]>([]);
  /** Per-role draft of checked library video ids. */
  const [selectionsByRole, setSelectionsByRole] = useState<
    Record<string, string[]>
  >({});
  /** Snapshot of assignments when entering Step 2 (per role). */
  const [baselineByRole, setBaselineByRole] = useState<
    Record<string, string[]>
  >({});
  const [activeRoleTab, setActiveRoleTab] = useState("");
  const [search, setSearch] = useState("");
  const [topicFilter, setTopicFilter] = useState("");
  const [publish, setPublish] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const accountTeamSelected = selectedTopRoles.some(isVideoAccountTeamTopRole);
  const draftRoleKeys = useMemo(
    () => resolveRoleKeys(selectedTopRoles, accountTeamSubRoles),
    [selectedTopRoles, accountTeamSubRoles]
  );
  const canContinue =
    selectedTopRoles.length > 0 &&
    draftRoleKeys.length > 0 &&
    (!accountTeamSelected || accountTeamSubRoles.length > 0);

  const roleKeys = confirmedRoleKeys;
  const activeRole = activeRoleTab || roleKeys[0] || "";
  const activeSelectedIds = selectionsByRole[activeRole] || [];
  const activeBaselineIds = baselineByRole[activeRole] || [];
  const activeBaselineSet = useMemo(
    () => new Set(activeBaselineIds),
    [activeBaselineIds]
  );

  const roleCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    VIDEO_ROLE_TOP_OPTIONS.forEach(option => {
      if (isVideoAccountTeamTopRole(option.value)) {
        const seen = new Set<string>();
        ACCOUNT_TEAM_ASSIGNMENT_ROLES.forEach(role => {
          (videosByRole[role] || []).forEach(video => {
            seen.add(video.library_video_id || video.video_id);
          });
        });
        counts[option.value] = seen.size;
      } else {
        counts[option.value] = (videosByRole[option.value] || []).length;
      }
    });
    ACCOUNT_TEAM_SUBROLE_OPTIONS.forEach(option => {
      counts[option.value] = (videosByRole[option.value] || []).length;
    });
    return counts;
  }, [videosByRole]);

  const filterTabs = useMemo(() => {
    const uncategorizedCount = libraryVideos.filter(
      video => !video.topic_id
    ).length;
    return [
      ...(topics as VideoTopic[]).map(topic => ({
        id: topic.id,
        label: topic.label,
        count: topic.video_count,
      })),
      ...(uncategorizedCount > 0
        ? [
            {
              id: UNCATEGORIZED_TOPIC_KEY,
              label: "Uncategorized",
              count: uncategorizedCount,
            },
          ]
        : []),
    ];
  }, [libraryVideos, topics]);

  const defaultTopicId = useMemo(() => {
    const withVideos = filterTabs.find(tab => tab.count > 0);
    return withVideos?.id || filterTabs[0]?.id || "";
  }, [filterTabs]);

  useEffect(() => {
    if (!isOpen) return;
    setStep("role");
    setSelectedTopRoles(initialTopRole ? [initialTopRole] : []);
    // Always pre-check every Account Team subrole (AE, CSM, SE, …).
    setAccountTeamSubRoles([...ACCOUNT_TEAM_ASSIGNMENT_ROLES]);
    setConfirmedRoleKeys([]);
    setSelectionsByRole({});
    setBaselineByRole({});
    setActiveRoleTab("");
    setSearch("");
    setPublish(true);
    setTopicFilter(defaultTopicId);
  }, [isOpen, initialTopRole, initialSubRole, defaultTopicId]);

  const visibleVideos = useMemo(() => {
    const query = search.trim().toLowerCase();
    return libraryVideos.filter(video => {
      const inTopic =
        topicFilter === UNCATEGORIZED_TOPIC_KEY
          ? !video.topic_id
          : video.topic_id === topicFilter;
      if (!inTopic) return false;
      if (!query) return true;
      return (video.title || "").toLowerCase().includes(query);
    });
  }, [libraryVideos, search, topicFilter]);

  const visibleIds = useMemo(
    () => visibleVideos.map(video => video.id),
    [visibleVideos]
  );

  const allVisibleSelected =
    visibleIds.length > 0 &&
    visibleIds.every(id => activeSelectedIds.includes(id));

  const roleDirtyFlags = useMemo(() => {
    const flags: Record<string, boolean> = {};
    roleKeys.forEach(role => {
      flags[role] =
        sortedKey(selectionsByRole[role] || []) !==
        sortedKey(baselineByRole[role] || []);
    });
    return flags;
  }, [roleKeys, selectionsByRole, baselineByRole]);

  const dirtyRoles = roleKeys.filter(role => roleDirtyFlags[role]);
  const hasChanges = dirtyRoles.length > 0;

  const totalToAdd = useMemo(() => {
    let count = 0;
    roleKeys.forEach(role => {
      const baseline = new Set(baselineByRole[role] || []);
      (selectionsByRole[role] || []).forEach(id => {
        if (!baseline.has(id)) count += 1;
      });
    });
    return count;
  }, [roleKeys, baselineByRole, selectionsByRole]);

  const totalToRemove = useMemo(() => {
    let count = 0;
    roleKeys.forEach(role => {
      const selected = new Set(selectionsByRole[role] || []);
      (baselineByRole[role] || []).forEach(id => {
        if (!selected.has(id)) count += 1;
      });
    });
    return count;
  }, [roleKeys, baselineByRole, selectionsByRole]);

  const toggleTopRole = (roleValue: string, checked: boolean) => {
    setSelectedTopRoles(prev => {
      if (checked) {
        if (prev.includes(roleValue)) return prev;
        return [...prev, roleValue];
      }
      return prev.filter(role => role !== roleValue);
    });
    if (isVideoAccountTeamTopRole(roleValue) && checked) {
      const nextSubs = [...ACCOUNT_TEAM_ASSIGNMENT_ROLES];
      setAccountTeamSubRoles(nextSubs);
      if (nextSubs[0]) setActiveRoleTab(nextSubs[0]);
    } else if (checked) {
      setActiveRoleTab(roleValue);
    }
  };

  const toggleAccountTeamSubrole = (roleValue: string, checked: boolean) => {
    if (!accountTeamSelected) {
      setSelectedTopRoles(roles =>
        roles.includes(ACCOUNT_TEAM_ROLE_FILTER)
          ? roles
          : [...roles, ACCOUNT_TEAM_ROLE_FILTER]
      );
    }
    setAccountTeamSubRoles(prev => {
      if (checked) {
        return prev.includes(roleValue) ? prev : [...prev, roleValue];
      }
      return prev.filter(role => role !== roleValue);
    });
    if (checked) setActiveRoleTab(roleValue);
  };

  const handleContinue = () => {
    if (!canContinue) return;
    const keys = resolveRoleKeys(selectedTopRoles, accountTeamSubRoles);
    if (keys.length === 0) return;

    const baselines: Record<string, string[]> = {};
    const selections: Record<string, string[]> = {};
    keys.forEach(role => {
      const ids = libraryIdsForRole(videosByRole, role);
      baselines[role] = ids;
      selections[role] = [...ids];
    });

    setConfirmedRoleKeys(keys);
    setBaselineByRole(baselines);
    setSelectionsByRole(selections);
    setActiveRoleTab(keys[0]);
    setSearch("");
    setTopicFilter(defaultTopicId);
    setStep("videos");
  };

  const handleBackToRoles = () => {
    setStep("role");
    setConfirmedRoleKeys([]);
    setSelectionsByRole({});
    setBaselineByRole({});
    setActiveRoleTab("");
  };

  const updateActiveSelection = (nextIds: string[]) => {
    if (!activeRole) return;
    setSelectionsByRole(prev => ({ ...prev, [activeRole]: nextIds }));
  };

  const toggleVideo = (id: string, checked: boolean) => {
    const next = checked
      ? [...activeSelectedIds, id]
      : activeSelectedIds.filter(item => item !== id);
    updateActiveSelection(next);
  };

  const selectAllVisible = () =>
    updateActiveSelection(
      Array.from(new Set([...activeSelectedIds, ...visibleIds]))
    );

  const clearAllVisible = () =>
    updateActiveSelection(
      activeSelectedIds.filter(id => !visibleIds.includes(id))
    );

  /** Copy the active role's video picks onto every other selected role. */
  const copyActiveToAllRoles = () => {
    if (!activeRole || roleKeys.length < 2) return;
    const source = [...(selectionsByRole[activeRole] || [])];
    setSelectionsByRole(prev => {
      const next = { ...prev };
      roleKeys.forEach(role => {
        next[role] = [...source];
      });
      return next;
    });
    toast.success(
      `Copied ${getLoginRoleLabel(activeRole)} selection to all selected roles`
    );
  };

  const handleSave = async () => {
    if (!hasChanges) {
      onClose();
      return;
    }

    setSubmitting(true);
    try {
      let addedOps = 0;
      let removedOps = 0;
      let rolesChanged = 0;

      for (const role of roleKeys) {
        const baseline = new Set(baselineByRole[role] || []);
        const selected = selectionsByRole[role] || [];
        const toAdd = selected.filter(id => !baseline.has(id));
        const toRemove = (baselineByRole[role] || []).filter(
          id => !selected.includes(id)
        );
        if (toAdd.length === 0 && toRemove.length === 0) continue;
        rolesChanged += 1;

        if (toAdd.length > 0) {
          const result = await dispatch(
            assignLibraryVideos({
              role,
              library_video_ids: toAdd,
              is_active: publish,
              quiet: true,
            })
          );
          if (!assignLibraryVideos.fulfilled.match(result)) return;
          addedOps += toAdd.length;
        }

        for (const libraryId of toRemove) {
          const videoIds = assignmentIdsForRoleLibrary(
            videosByRole,
            role,
            libraryId
          );
          for (const videoId of videoIds) {
            const result = await dispatch(deleteVideo(videoId));
            if (deleteVideo.rejected.match(result)) {
              toast.error(
                (result.payload as string) || "Failed to remove assignment"
              );
              return;
            }
            removedOps += 1;
          }
        }
      }

      // One summary toast for the whole multi-role save.
      if (addedOps > 0 && removedOps === 0) {
        toast.success(
          rolesChanged > 1
            ? `Videos assigned to ${rolesChanged} roles`
            : addedOps === 1
              ? "Video assigned"
              : "Videos assigned"
        );
      } else if (addedOps === 0 && removedOps > 0) {
        toast.success(
          removedOps === 1 ? "Video removed from role" : "Assignments updated"
        );
      } else if (addedOps > 0 && removedOps > 0) {
        toast.success(
          rolesChanged > 1
            ? `Assignments updated for ${rolesChanged} roles`
            : "Assignments updated"
        );
      }

      await Promise.all([
        dispatch(fetchAdminVideos({})),
        dispatch(fetchLibraryVideos()),
      ]);

      onAssigned?.(
        browseTargetFromSelection(
          selectedTopRoles,
          accountTeamSubRoles,
          activeRole || roleKeys[0]
        )
      );
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const libraryIsEmpty = libraryVideos.length === 0;
  const busy = saving || deleteVideoLoading || submitting;

  const actionLabel = (() => {
    if (!hasChanges) return "Save changes";
    if (totalToAdd > 0 && totalToRemove === 0) {
      return `Assign ${totalToAdd} ${totalToAdd === 1 ? "video" : "videos"}`;
    }
    if (totalToAdd === 0 && totalToRemove > 0) {
      return `Remove ${totalToRemove} ${
        totalToRemove === 1 ? "video" : "videos"
      }`;
    }
    return `Save changes (${dirtyRoles.length} ${
      dirtyRoles.length === 1 ? "role" : "roles"
    })`;
  })();

  const stepIndicator = (
    <div className="assign-videos-modal__steps" aria-label="Assign steps">
      <span
        className={`assign-videos-modal__step${
          step === "role" ? " assign-videos-modal__step--active" : ""
        }${step === "videos" ? " assign-videos-modal__step--done" : ""}`}
      >
        <span className="assign-videos-modal__step-num">1</span>
        Roles
      </span>
      <span className="assign-videos-modal__step-sep" aria-hidden />
      <span
        className={`assign-videos-modal__step${
          step === "videos" ? " assign-videos-modal__step--active" : ""
        }`}
      >
        <span className="assign-videos-modal__step-num">2</span>
        Videos
      </span>
    </div>
  );

  const selectedSummary =
    selectedTopRoles.length === 0
      ? "Select at least one role to continue."
      : accountTeamSelected && accountTeamSubRoles.length === 0
        ? "Pick at least one Account Team subrole."
        : `${selectedTopRoles.length} ${
            selectedTopRoles.length === 1 ? "role" : "roles"
          } selected`;

  const roleStepFooter = (
    <>
      <p className="assign-videos-modal__footer-hint">{selectedSummary}</p>
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        disabled={!canContinue || libraryIsEmpty}
        onClick={handleContinue}
      >
        Continue
      </Button>
    </>
  );

  const saveFooter = (
    <>
      <div className="mr-auto flex items-center gap-3">
        <ToggleSwitch
          checked={publish}
          onChange={setPublish}
          id="assign-videos-publish"
        />
        <span className="text-sm text-gray-700">Publish immediately</span>
      </div>
      <Button variant="secondary" onClick={handleBackToRoles} disabled={busy}>
        <span className="inline-flex items-center gap-1">
          <ChevronLeft size={16} />
          Back
        </span>
      </Button>
      <Button variant="secondary" onClick={onClose} disabled={busy}>
        Cancel
      </Button>
      <Button
        variant="primary"
        loading={busy}
        disabled={!hasChanges || busy || roleKeys.length === 0}
        onClick={handleSave}
      >
        {actionLabel}
      </Button>
    </>
  );

  const activeRoleLabel = getLoginRoleLabel(activeRole);
  const editingExisting = activeBaselineIds.length > 0;

  const modalTitle =
    step === "role"
      ? "Assign videos"
      : editingExisting
        ? `Edit videos for ${activeRoleLabel}`
        : `Choose videos for ${activeRoleLabel}`;

  const modalSubtitle = stepIndicator;

  const modalFooter = step === "role" ? roleStepFooter : saveFooter;

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={onClose}
      title={modalTitle}
      subtitle={modalSubtitle}
      size="4xl"
      className={`assign-videos-modal${
        step === "role" ? " assign-videos-modal--roles" : ""
      }`}
      contentClassName="overflow-hidden flex flex-col !p-0"
      showHeader={true}
      footer={modalFooter}
    >
      {libraryIsEmpty ? (
        <p className="assign-videos-modal__empty-library">
          The library is empty. Add videos in the Video Library tab first.
        </p>
      ) : step === "role" ? (
        <div className="assign-videos-modal__roles">
          <p className="assign-videos-modal__panel-hint">
            Select who should see videos. For Account Team, pick the subroles
            you want, then set videos for each in the next step.
          </p>
          <ul className="assign-role-list" role="listbox" aria-label="Roles">
            {VIDEO_ROLE_TOP_OPTIONS.map(option => {
              const isAccountTeam = isVideoAccountTeamTopRole(option.value);
              const isChecked = selectedTopRoles.includes(option.value);
              const count = roleCounts[option.value] ?? 0;
              return (
                <li key={option.value} className="assign-role-list__item">
                  <div
                    role="option"
                    aria-selected={isChecked}
                    tabIndex={0}
                    className={`assign-role-row${
                      isChecked ? " assign-role-row--checked" : ""
                    }`}
                    onClick={() => toggleTopRole(option.value, !isChecked)}
                    onKeyDown={event => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggleTopRole(option.value, !isChecked);
                      }
                    }}
                  >
                    <span
                      onClick={event => event.stopPropagation()}
                      onKeyDown={event => event.stopPropagation()}
                    >
                      <CustomCheckbox
                        checked={isChecked}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          toggleTopRole(option.value, e.target.checked)
                        }
                      />
                    </span>
                    <span className="assign-role-row__label">
                      {option.label}
                    </span>
                    <span
                      className={`assign-role-row__badge${
                        count > 0 ? " assign-role-row__badge--filled" : ""
                      }`}
                    >
                      {count} assigned
                    </span>
                  </div>

                  {isAccountTeam && isChecked && (
                    <div className="assign-role-subroles">
                      <div className="assign-role-subroles__grid">
                        {ACCOUNT_TEAM_SUBROLE_OPTIONS.map(sub => {
                          const subChecked = accountTeamSubRoles.includes(
                            sub.value
                          );
                          return (
                            <label
                              key={sub.value}
                              className={`assign-role-subroles__chip${
                                subChecked
                                  ? " assign-role-subroles__chip--on"
                                  : ""
                              }`}
                            >
                              <CustomCheckbox
                                checked={subChecked}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                  toggleAccountTeamSubrole(
                                    sub.value,
                                    e.target.checked
                                  )
                                }
                              />
                              <span className="assign-role-subroles__name">
                                {sub.label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <div className="assign-videos-modal__videos">
          <div className="assign-videos-modal__role-bar">
            <div className="assign-videos-modal__role-bar-top">
              <p className="assign-videos-modal__role-bar-title">
                {roleKeys.length === 1
                  ? "Selected role"
                  : `${roleKeys.length} roles — pick videos for each`}
              </p>
              <div className="assign-videos-modal__role-bar-actions">
                {roleKeys.length > 1 && (
                  <button
                    type="button"
                    className="assign-videos-modal__ghost-btn"
                    onClick={copyActiveToAllRoles}
                    title="Copy this role's checked videos to every other selected role"
                  >
                    Copy to all
                  </button>
                )}
                <button
                  type="button"
                  className="assign-videos-modal__change-role-btn"
                  onClick={handleBackToRoles}
                >
                  <ArrowLeftRight size={14} strokeWidth={2.25} aria-hidden />
                  Change role
                </button>
              </div>
            </div>
            <div className="assign-role-tabs-wrap">
              <div
                className="assign-role-tabs"
                role="tablist"
                aria-label="Roles to configure"
              >
                {roleKeys.map(role => {
                  const count = (selectionsByRole[role] || []).length;
                  const dirty = roleDirtyFlags[role];
                  const isActive = role === activeRole;
                  return (
                    <button
                      key={role}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={`assign-role-tabs__tab${
                        isActive ? " assign-role-tabs__tab--active" : ""
                      }${dirty ? " assign-role-tabs__tab--dirty" : ""}`}
                      onClick={() => setActiveRoleTab(role)}
                    >
                      <span className="assign-role-tabs__label">
                        {getLoginRoleLabel(role)}
                      </span>
                      <span className="assign-role-tabs__count">{count}</span>
                      {dirty && (
                        <span className="assign-role-tabs__dot" aria-hidden />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="assign-videos-modal__content">
            <aside className="assign-videos-modal__topics" aria-label="Topics">
              <p className="assign-videos-modal__topics-title">Topics</p>
              <FilterChips
                chips={filterTabs}
                activeId={topicFilter}
                onChange={setTopicFilter}
                ariaLabel="Filter library videos by topic"
                orientation="vertical"
              />
            </aside>

            <div className="assign-videos-modal__main">
              <div className="assign-video-toolbar">
                <div className="assign-video-toolbar__left">
                  <p className="assign-video-toolbar__role">
                    {activeRoleLabel}
                  </p>
                  <p className="assign-video-toolbar__meta">
                    {activeSelectedIds.length} selected
                    {visibleIds.length > 0
                      ? ` · ${visibleIds.length} in this topic`
                      : ""}
                  </p>
                </div>
                <div className="assign-video-toolbar__actions">
                  <WorkspaceSearch
                    value={search}
                    onChange={setSearch}
                    placeholder="Search by title…"
                    ariaLabel="Search videos by title"
                  />
                  <button
                    type="button"
                    className="assign-video-toolbar__btn"
                    disabled={visibleIds.length === 0 || allVisibleSelected}
                    onClick={selectAllVisible}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="assign-video-toolbar__btn"
                    disabled={
                      visibleIds.length === 0 ||
                      !visibleIds.some(id => activeSelectedIds.includes(id))
                    }
                    onClick={clearAllVisible}
                  >
                    Clear all
                  </button>
                </div>
              </div>

              {visibleVideos.length === 0 ? (
                <p className="assign-videos-modal__empty">
                  No videos match these filters.
                </p>
              ) : (
                <div className="assign-video-grid">
                  {visibleVideos.map(video => {
                    const wasAssigned = activeBaselineSet.has(video.id);
                    const isChecked = activeSelectedIds.includes(video.id);
                    const thumbnailUrl = getYouTubeThumbnailUrl(
                      video.video_url
                    );
                    const pendingAdd = !wasAssigned && isChecked;
                    const pendingRemove = wasAssigned && !isChecked;
                    const existingRow = (videosByRole[activeRole] || []).find(
                      row => row.library_video_id === video.id
                    );
                    const isPublished = existingRow
                      ? (existingRow.is_active ?? true)
                      : publish;
                    return (
                      <div
                        key={video.id}
                        role="button"
                        tabIndex={0}
                        className={`assign-video-card${
                          isChecked ? " assign-video-card--checked" : ""
                        }${
                          pendingRemove ? " assign-video-card--removing" : ""
                        }`}
                        onClick={() => toggleVideo(video.id, !isChecked)}
                        onKeyDown={event => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            toggleVideo(video.id, !isChecked);
                          }
                        }}
                      >
                        <span
                          className="assign-video-card__check"
                          onClick={event => event.stopPropagation()}
                          onKeyDown={event => event.stopPropagation()}
                        >
                          <CustomCheckbox
                            checked={isChecked}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => toggleVideo(video.id, e.target.checked)}
                          />
                        </span>
                        <div className="assign-video-card__thumb">
                          {thumbnailUrl ? (
                            <img src={thumbnailUrl} alt="" />
                          ) : (
                            <Play size={18} className="text-gray-400" />
                          )}
                        </div>
                        <div className="assign-video-card__body">
                          <p className="assign-video-card__title">
                            {video.title || "Untitled Video"}
                          </p>
                          <div className="assign-video-card__meta">
                            {pendingRemove ? (
                              <span className="assign-video-card__badge assign-video-card__badge--remove">
                                Will remove
                              </span>
                            ) : (
                              <span
                                className={`assign-video-card__badge${
                                  isPublished
                                    ? " assign-video-card__badge--published"
                                    : " assign-video-card__badge--draft"
                                }`}
                              >
                                {isPublished ? "Published" : "Unpublished"}
                              </span>
                            )}
                            {pendingAdd && (
                              <span className="assign-video-card__badge assign-video-card__badge--add">
                                Will add
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </CommonModal>
  );
};

export default AssignVideosModal;
